import { useEffect, useState } from 'react';
import { api, errMsg } from '../lib/api';

interface ArticleRow {
  id: string;
  title: string;
  status: string;
  content: string;
  createdAt: number;
}

/** 馆主/可编辑成员：追思文章的增删改查 */
export default function ArticleManager({ memorialId }: { memorialId: string }) {
  const [items, setItems] = useState<ArticleRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('published');
  const [notice, setNotice] = useState('');
  const [drafting, setDrafting] = useState(false);

  async function load() {
    const res = await api<{ items: ArticleRow[] }>(`/articles/mine?memorialId=${memorialId}`);
    if (res.ok && res.data) setItems(res.data.items);
  }

  useEffect(() => {
    load();
  }, [memorialId]);

  function reset() {
    setEditing(null);
    setTitle('');
    setContent('');
    setStatus('published');
  }

  async function save() {
    if (!title.trim() || !content.trim()) {
      setNotice('标题和内容不能为空');
      return;
    }
    const payload = { title: title.trim(), content: content.trim(), status };
    const res = editing
      ? await api(`/articles/${editing}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await api('/articles', {
          method: 'POST',
          body: JSON.stringify({ ...payload, memorialId }),
        });

    if (!res.ok) {
      setNotice(`保存失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice(editing ? '文章已更新' : '文章已保存');
    reset();
    load();
  }

  /** 馆主「AI 帮我起稿」：生成草稿，家属修改后才可发布 */
  async function aiDraft() {
    setDrafting(true);
    setNotice('');
    const res = await api<{ id: string; title: string; content: string }>(
      '/articles/ai-draft',
      { method: 'POST', body: JSON.stringify({ memorialId }) }
    );
    setDrafting(false);
    if (!res.ok) {
      setNotice(`AI 起稿失败：${errMsg(res, '未知错误')}`);
      return;
    }
    const d = res.data!;
    setEditing(d.id);
    setTitle(d.title);
    setContent(d.content);
    setStatus('draft');
    setNotice('已生成 AI 草稿，请修改后再发布（不可直接发布原文）');
  }

  async function remove(id: string) {
    if (!window.confirm('确定删除这篇文章？')) return;
    const res = await api(`/articles/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      setNotice(`删除失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice('文章已删除');
    load();
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-ink-900">追思文章（{items.length}）</p>
        <div className="flex gap-2">
          <button
            onClick={aiDraft}
            disabled={drafting}
            className="rounded-full bg-candle-300/40 px-3 py-1 text-[11px] text-ink-800 hover:bg-candle-300/60 disabled:opacity-50"
          >
            {drafting ? '生成中…' : 'AI 帮我起稿'}
          </button>
          <button
            onClick={reset}
            className="rounded-full bg-ink-900 px-3 py-1 text-[11px] text-ink-50"
          >
            + 写新文章
          </button>
        </div>
      </div>

      {notice && (
        <p className="mb-3 rounded-lg bg-candle-300/20 px-3 py-2 text-xs text-ink-700">{notice}</p>
      )}

      <div className="mb-4 rounded-xl border border-ink-200 bg-ink-50/50 p-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="文章标题"
          className="mb-2 h-10 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm outline-none"
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          placeholder="正文…"
          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none"
        />
        <div className="mt-2 flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
            className="h-9 rounded-lg border border-ink-200 bg-white px-2 text-xs outline-none"
          >
            <option value="published">发布</option>
            <option value="draft">草稿</option>
          </select>
          <button
            onClick={save}
            className="rounded-full bg-ink-900 px-4 py-1.5 text-xs text-ink-50"
          >
            {editing ? '保存修改' : '发布文章'}
          </button>
          {editing && (
            <button onClick={reset} className="rounded-full px-3 py-1.5 text-xs text-ink-500">
              取消编辑
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-ink-500">还没有文章</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-ink-900">{a.title}</p>
                <p className="mt-0.5 text-[10px] text-ink-500">
                  {a.status === 'published' ? '已发布' : '草稿'} ·{' '}
                  {new Date(a.createdAt * 1000).toLocaleDateString('zh-CN')}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  onClick={() => {
                    setEditing(a.id);
                    setTitle(a.title);
                    setContent(a.content);
                    setStatus(a.status === 'draft' ? 'draft' : 'published');
                  }}
                  className="rounded-full bg-ink-100 px-3 py-1 text-[10px] text-ink-700"
                >
                  编辑
                </button>
                <button
                  onClick={() => remove(a.id)}
                  className="rounded-full bg-red-50 px-3 py-1 text-[10px] text-red-600"
                >
                  删除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
